import { IdentifyTraits, RudderAnalytics } from '@rudderstack/analytics-js';
import { Children, ReactNode, isValidElement } from 'react';

import config from '../../config';

const rudderanalytics = new RudderAnalytics();

// Must match the consent ID on each destination's RudderStack consent filter (LE-19149)
const CONSENT_CATEGORIES = ['marketing'];
const ALLOWED_CONSENT = { allowedConsentIds: CONSENT_CATEGORIES, deniedConsentIds: [] };
const DENIED_CONSENT = { allowedConsentIds: [], deniedConsentIds: CONSENT_CATEGORIES };
const ANONYMOUS_ID_TIMEOUT_MS = 1500;

let isConsentManaged = false;

// Calls made before load() queue in the SDK and get the load-time stamp
const loadRudderStack = (consent?: { isDenied: boolean }) => {
  if (!config.rudderstack.writeKey) {
    return;
  }

  isConsentManaged = !!consent;
  rudderanalytics.load(config.rudderstack.writeKey, config.rudderstack.dataPlaneUrl, {
    useServerSideCookies: true,
    storage: {
      cookie: {
        domain: config.rudderstack.cookieDomain,
        secure: true,
        samesite: 'None',
      },
    },
    consentManagement: consent && {
      enabled: true,
      provider: 'custom',
      ...(consent.isDenied ? DENIED_CONSENT : ALLOWED_CONSENT),
    },
  });
};

// The SDK stamps an event when it's created, so this must run before the events it covers
const denyConsent = () => {
  if (isConsentManaged) {
    rudderanalytics.consent({ consentManagement: DENIED_CONSENT });
  }
};

const waitForAnonymousId = async () => {
  if (!config.rudderstack.writeKey || rudderanalytics.getAnonymousId()) {
    return;
  }

  await new Promise<void>((resolve) => {
    // SDK event fired once the anon id exists; ready() also waits on destination scripts
    document.addEventListener('RSA_Initialised', () => resolve(), { once: true });
    setTimeout(resolve, ANONYMOUS_ID_TIMEOUT_MS);
  });
};

const identify = (traits: IdentifyTraits) =>
  // Must match RudderStack's destination display name exactly; unknown keys are silently ignored
  rudderanalytics.identify(traits, { integrations: { 'Google Analytics 4 (GA4)': false } });

enum RudderEvent {
  // General Events
  Click = 'Click', // Manually called see trackClickEvent
  Error = 'Error',
  ErrorRetry = 'ErrorRetry',

  // Flow Events
  AccordionView = 'Accordion View',
  AccountExists = 'Account Exists',
  CreditSoftPull = 'Credit Soft Pull',
  DoubleTaxModalShown = 'Double Tax Modal Shown (names must match modal)',
  FlowTest = 'FlowTest',
  ManualLienholderAutoMatch = 'Manual Lienholder Auto Match',
  OtherLienholderModalOpen = 'Other Lienholder Modal Open',
  OtherLienholderSelected = 'Other Lienholder Selected',
  VinProvided = 'Vin Provided',

  // Conversion Events
  RStackWebStartFlow = 'Rstack - Web Start Flow',
  RStackWebEstimate = 'Rstack - Web Estimate',
  RStackWebEstimateEnhanced = 'Rstack - Web Estimate Enhanced',
  RStackWebFinishFlow = 'Rstack - Web Finish Flow',

  // Signing Events
  SigningExperienceOpened = 'Signing Experience Opened',
  StartedSigning = 'Started Signing',
  FinishedSigning = 'Finished Signing',
}

type TrackClickEventArgs = {
  object: string;
  elementType: 'button' | 'link' | 'radio' | 'checkbox' | 'dropdown' | 'input';
  children: ReactNode;
};

const trackClickEvent = ({ object, elementType, children }: TrackClickEventArgs) => {
  const getLabel = (item: ReactNode): string => {
    let itemLabel = '';

    Children.forEach(item, (child) => {
      if (typeof child === 'string') {
        itemLabel += child;
      } else if (typeof child === 'number') {
        itemLabel += child.toString();
      } else if (isValidElement(child) && child.props.children) {
        itemLabel += getLabel(child.props.children);
      }
    });

    return itemLabel;
  };

  rudderanalytics.track(RudderEvent.Click, {
    object,
    elementType,
    label: getLabel(children),
  });
};

export {
  denyConsent,
  identify,
  loadRudderStack,
  rudderanalytics,
  RudderEvent,
  trackClickEvent,
  waitForAnonymousId,
};
